import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ProductSpec } from "@/data/phoneSpecs";
import { useEffect, useRef } from "react";

const isEmptySpecs = (specs: any) => !specs || Object.keys(specs).length === 0;

export const useProducts = () => {
  const fixedRef = useRef<Set<string>>(new Set());

  const query = useQuery({
    queryKey: ["products"],
    queryFn: async (): Promise<ProductSpec[]> => {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("active", true)
        .eq("is_archived", false)
        .order("created_at", { ascending: true });

      if (error) throw error;

      return (data || []).map((p: any) => ({
        id: p.id,
        name: p.name,
        color: p.color,
        price: Number(p.price).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
        priceNum: Number(p.price),
        image: p.image_url,
        stock: p.stock,
        specs: p.specs as ProductSpec["specs"],
        ratings: p.ratings as ProductSpec["ratings"],
      }));
    },
  });

  // Auto-fix products with missing specs/ratings via edge function (uses service role)
  useEffect(() => {
    if (!query.data) return;
    const missing = query.data.filter(
      (p) => (isEmptySpecs(p.specs) || isEmptySpecs(p.ratings)) && !fixedRef.current.has(p.id)
    );
    if (missing.length === 0) return;

    missing.forEach((p) => fixedRef.current.add(p.id));

    const fixAll = async () => {
      for (const p of missing) {
        try {
          const { error } = await supabase.functions.invoke("fetch-phone-specs", {
            body: { phone_name: p.name, product_id: p.id },
          });
          if (error) console.error("Auto-fix specs failed for", p.name, error);
        } catch (e) {
          console.error("Auto-fix specs failed for", p.name, e);
        }
      }
      query.refetch();
    };
    fixAll();
  }, [query.data]);

  return query;
};
